/**
 * Admin-facing brand identifier profile: sells, SKU/MPN, and barcode rules.
 * Sourced from brandCatalogIdentity + brandSells (Brand Identifier Formats).
 */

import { brandCatalogIdentity, type BarcodeModel, type IdentityConfidence } from './brandCatalogIdentity';
import { barcodeModelLabel, brandIdentifierPolicy } from './brandIdentifierPolicy';
import { brandSells, formatSellLabel } from './brandSells';

export type BrandCatalogProfile = {
  name: string;
  brandId: string;
  sells: string[];
  sellsLabel: string;
  skuLooksLike: string;
  skuExample?: string;
  mpnRule?: string;
  barcodeModel: BarcodeModel;
  barcodeModelLabel: string;
  barcodeModelExplain: string;
  upcLooksLike: string;
  eanLooksLike: string;
  gtinLooksLike: string;
  gs1Notes?: string;
  scraperHint?: string;
  source?: string;
  confidence?: IdentityConfidence;
  confidenceLabel: string;
  researched: boolean;
};

const BARCODE_EXPLAIN: Record<BarcodeModel, string> = {
  'upc-a':
    'Vendor publishes a 12-digit US UPC-A. Motzu derives EAN as 0 + UPC and GTIN as the UPC padded to 14. That is the same GS1 number, not a second product.',
  'ean-as-upc':
    'Vendor publishes EAN-13 only (often GS1 CN 69…). Store that 13-digit value in UPC. Do not invent a 12-digit UPC-A.',
  'independent-regional':
    'US UPC-A and a regional EAN/JAN are different GS1 numbers for the same SKU. Do not invent EAN by zero-padding the UPC, and do not overwrite the UPC with the JAN.',
  unknown:
    'Not researched as a hard rule. Default GS1 pairing only (0 + UPC when a 12-digit UPC exists). Confirm before treating a mismatch as an error.',
};

const CONFIDENCE_LABEL: Record<IdentityConfidence, string> = {
  motzu: 'Motzu-verified',
  researched: 'Researched',
  inferred: 'Inferred',
};

export function barcodeModelExplain(model?: BarcodeModel): string {
  return BARCODE_EXPLAIN[model || 'unknown'];
}

export function buildBrandCatalogProfile(
  brandId?: string | null,
  brand?: string | null,
  sellsOverride?: string[] | null,
): BrandCatalogProfile | undefined {
  const key = String(brandId || brand || '').trim();
  if (!key) return undefined;

  const catalog = brandCatalogIdentity(brandId, brand);
  const policy = brandIdentifierPolicy(brandId, brand);
  const sellsRaw =
    (sellsOverride && sellsOverride.length > 0 ? sellsOverride : brandSells(brandId, brand)) || [];
  const sells = sellsRaw.map((value) => formatSellLabel(value));

  return {
    name: catalog?.name || String(brand || brandId || '').trim(),
    brandId: catalog?.brandId || policy.brandKey,
    sells,
    sellsLabel: sells.join(' · ') || 'Not listed',
    skuLooksLike: policy.skuLooksLike,
    skuExample: policy.skuExample,
    mpnRule: policy.mpnRule,
    barcodeModel: policy.barcodeModel,
    barcodeModelLabel: barcodeModelLabel(policy.barcodeModel) || 'Default GS1',
    barcodeModelExplain: barcodeModelExplain(policy.barcodeModel),
    upcLooksLike: policy.upcLooksLike,
    eanLooksLike: policy.eanLooksLike,
    gtinLooksLike: policy.gtinLooksLike,
    gs1Notes: policy.gs1Notes,
    scraperHint: policy.scraperHint,
    source: catalog?.source,
    confidence: policy.confidence,
    confidenceLabel: policy.confidence ? CONFIDENCE_LABEL[policy.confidence] : 'Unresearched',
    researched: Boolean(catalog),
  };
}
